import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  Copy,
  ArrowSquareOut,
  LockKey,
  Fire,
  Timer,
  Eye,
  Key,
  Warning,
} from "@phosphor-icons/react";

export default function PasteShare() {
  const { id } = useParams();
  const location = useLocation();
  const [keyB64, setKeyB64] = useState("");
  const meta = location.state?.meta;

  useEffect(() => {
    const frag = window.location.hash.replace(/^#/, "");
    setKeyB64(frag);
  }, []);

  const shareUrl = `${window.location.origin}/p/${id}#${keyB64}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard.`);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-12">
      <div className="animate-fade-up">
        <div className="label-overline mb-3">// Step 02 / Distribute</div>
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle size={28} weight="bold" className="text-secure" />
          <h1
            data-testid="share-title"
            className="font-display text-3xl md:text-4xl font-bold tracking-tighter"
          >
            Encrypted. Sealed.
          </h1>
        </div>
        <p className="font-mono text-sm text-muted-foreground max-w-2xl">
          Your paste lives behind two locks. Share the URL below — anyone with
          it (and the password, if set) can read it. We can't.
        </p>
      </div>

      {/* Share link */}
      <div className="mt-8 border border-foreground/15 bg-card">
        <div className="border-b border-foreground/15 px-4 py-3 flex items-center justify-between">
          <span className="label-overline flex items-center gap-2">
            <LockKey size={12} weight="bold" /> Share link
          </span>
          <span className="text-[10px] font-mono text-muted-foreground">
            key lives in URL fragment after #
          </span>
        </div>
        <div className="p-4">
          <div
            data-testid="share-url-display"
            className="font-mono text-sm break-all bg-secondary/60 p-4 border border-foreground/10 select-all"
          >
            {shareUrl}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <button
              data-testid="copy-link-btn"
              onClick={() => copy(shareUrl, "Share link")}
              className="h-12 bg-foreground text-background font-bold uppercase tracking-[0.18em] text-xs inline-flex items-center justify-center gap-2 hover:bg-neutral-800 transition-colors"
            >
              <Copy size={16} weight="bold" /> Copy link
            </button>
            <a
              data-testid="open-paste-link"
              href={`/p/${id}#${keyB64}`}
              className="h-12 border border-foreground text-foreground font-bold uppercase tracking-[0.18em] text-xs inline-flex items-center justify-center gap-2 hover:bg-secondary transition-colors"
            >
              <ArrowSquareOut size={16} weight="bold" /> Open
            </a>
          </div>
        </div>
      </div>

      {/* Properties */}
      <div className="mt-8 grid sm:grid-cols-2 gap-px border border-foreground/15 bg-foreground/15">
        <PropertyCell
          icon={<Timer size={14} weight="bold" />}
          label="Expires"
          value={
            meta?.expires_at
              ? new Date(meta.expires_at).toLocaleString()
              : "Never"
          }
          testid="prop-expires"
        />
        <PropertyCell
          icon={<Eye size={14} weight="bold" />}
          label="View limit"
          value={meta?.view_limit ? `${meta.view_limit} reads` : "Unlimited"}
          testid="prop-views"
        />
        <PropertyCell
          icon={<Fire size={14} weight="bold" />}
          label="Burn after read"
          value={meta?.burn_after_read ? "Yes" : "No"}
          accent={meta?.burn_after_read ? "burn" : null}
          testid="prop-burn"
        />
        <PropertyCell
          icon={<Key size={14} weight="bold" />}
          label="Paste ID"
          value={id}
          testid="prop-id"
        />
      </div>

      {meta?.burn_after_read && (
        <div
          data-testid="burn-warning"
          className="mt-6 border border-burn bg-burn/5 p-4 flex items-start gap-3"
        >
          <Warning
            size={20}
            weight="bold"
            className="text-burn flex-shrink-0 mt-0.5"
          />
          <p className="font-mono text-xs leading-relaxed">
            <span className="font-bold text-burn">BURN MODE ACTIVE.</span> The
            very first time anyone opens this link the paste will be{" "}
            <em>permanently destroyed</em>. Don't open it yourself unless this
            is your intended read.
          </p>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="create-another-link"
          className="text-xs font-mono uppercase tracking-[0.18em] border-b border-foreground hover:opacity-60"
        >
          ← Create another
        </Link>
      </div>
    </div>
  );
}

function PropertyCell({ icon, label, value, accent, testid }) {
  return (
    <div className="bg-background p-5" data-testid={testid}>
      <div className="label-overline flex items-center gap-2">
        {icon}
        {label}
      </div>
      <div
        className={`mt-2 font-mono text-sm break-all ${
          accent === "burn" ? "text-burn font-bold" : "text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
